import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Check, Loader2, FileText, Plus, Trash2, Copy as CopyIcon, AlertCircle } from "lucide-react";
import { MORTGAGE_FORM_SECTIONS, sectionsForType } from "../forms/mortgageSchema";
import { findProduct } from "../forms/productCatalog";
import { SignaturePad } from "../components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDDMMYYYY = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const fmtGBP = (n) => {
  if (n === "" || n == null) return "";
  const num = Number(n);
  if (!isFinite(num)) return "";
  return `£${num.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const INPUT_CLS =
  "w-full bg-white border border-forest/15 px-3.5 py-3 text-[15px] rounded-sm focus:border-gold focus:outline-none disabled:bg-cream-3 disabled:text-sage";

const Field = ({ field, value, onChange, path, disabled, error }) => {
  const id = `${path}-${field.id}`;
  const t = field.type;
  const cls = `${INPUT_CLS} ${error ? "border-red-400 bg-red-50/40" : ""}`;
  const common = { id, "data-testid": id, disabled, className: cls };
  if (t === "textarea")
    return <textarea rows={3} {...common} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (t === "yesno")
    return (
      <select {...common} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    );
  if (t === "select")
    return (
      <select {...common} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  if (t === "postcode")
    return (
      <input
        type="text"
        inputMode="text"
        maxLength={8}
        {...common}
        value={value || ""}
        onChange={(e) => onChange(e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, "").slice(0, 8))}
        placeholder="e.g. SW1A 1AA"
      />
    );
  if (t === "money") {
    return (
      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-forest/50 pointer-events-none">£</span>
        <input
          type="number"
          step="0.01"
          min="0"
          inputMode="decimal"
          {...common}
          className={`${cls} pl-7`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }
  const inputType = { number: "number", date: "date", email: "email", phone: "tel" }[t] || "text";
  return (
    <input
      type={inputType}
      {...common}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const FieldWrap = ({ label, help, full, required, children, error, dateHint }) => (
  <label className={`block ${full ? "sm:col-span-2" : ""}`}>
    <span className="block text-[10.5px] tracking-[0.18em] uppercase font-semibold text-forest mb-1.5">
      {label}{required && <span className="text-gold-dark ml-1" aria-label="required">*</span>}
    </span>
    {children}
    {dateHint && <span className="block text-[11px] text-forest/50 mt-1">{dateHint}</span>}
    {help && !error && <span className="block text-[11.5px] text-sage mt-1 leading-relaxed">{help}</span>}
    {error && (
      <span className="flex items-center gap-1 text-[11.5px] text-rose-700 mt-1">
        <AlertCircle size={12} /> {error}
      </span>
    )}
  </label>
);

const shouldShow = (field, sectionValues) =>
  !field.showIf ||
  (sectionValues?.[field.showIf.field] || "") === field.showIf.equals;

const monthsBetween = (from, to) => {
  if (!from) return 0;
  const start = new Date(from);
  const end = to ? new Date(to) : new Date();
  if (isNaN(start) || isNaN(end) || end < start) return 0;
  return Math.max(
    0,
    (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth())
  );
};

const AddressHistoryEditor = ({ value, onChange, disabled, testid }) => {
  const rows = Array.isArray(value) ? value : [];
  const update = (idx, k, v) => {
    const next = rows.slice();
    next[idx] = { ...(next[idx] || {}), [k]: v };
    onChange(next);
  };
  const add = () => onChange([...rows, {}]);
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const totalMonths = rows.reduce((sum, r) => sum + monthsBetween(r.from, r.to), 0);
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const covers3Years = totalMonths >= 36;
  return (
    <div className="sm:col-span-2 space-y-2" data-testid={testid || "address-history-editor"}>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-[10px] tracking-[0.14em] uppercase text-forest/55">
              <th className="text-left px-2 py-1 w-[42%]">Previous Address</th>
              <th className="text-left px-2 py-1 w-[14%]">Postcode</th>
              <th className="text-left px-2 py-1 w-[18%]">From</th>
              <th className="text-left px-2 py-1 w-[18%]">To</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-forest/8 align-top">
                <td className="px-1 py-1">
                  <textarea rows={2} disabled={disabled} value={r.address || ""}
                    onChange={(e) => update(i, "address", e.target.value)}
                    placeholder="House / flat, street, town"
                    className="w-full bg-cream-3 border border-forest/15 px-2 py-1.5 focus:border-gold focus:outline-none resize-y" />
                </td>
                <td className="px-1 py-1">
                  <input type="text" disabled={disabled} value={r.postcode || ""} maxLength={8}
                    onChange={(e) => update(i, "postcode", e.target.value.toUpperCase().replace(/[^A-Z0-9 ]/g, "").slice(0, 8))}
                    placeholder="SW1A 1AA"
                    className="w-full bg-cream-3 border border-forest/15 px-2 py-1.5 focus:border-gold focus:outline-none" />
                </td>
                <td className="px-1 py-1">
                  <input type="date" disabled={disabled} value={r.from || ""} onChange={(e) => update(i, "from", e.target.value)}
                    className="w-full bg-cream-3 border border-forest/15 px-2 py-1.5 focus:border-gold focus:outline-none" />
                </td>
                <td className="px-1 py-1">
                  <input type="date" disabled={disabled} value={r.to || ""} onChange={(e) => update(i, "to", e.target.value)}
                    className="w-full bg-cream-3 border border-forest/15 px-2 py-1.5 focus:border-gold focus:outline-none" />
                </td>
                <td className="px-1 py-1 text-center">
                  <button type="button" disabled={disabled} onClick={() => remove(i)} className="p-1.5 text-forest/50 hover:text-red-600">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-2 py-3 text-sage text-center">No previous addresses yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        {!disabled && (
          <button type="button" onClick={add} data-testid={`${testid || "address-history-editor"}-add`}
            className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-forest hover:text-gold-dark">
            <Plus size={13} /> Add Previous Address
          </button>
        )}
        {rows.length > 0 && (
          <span
            data-testid={`${testid || "address-history-editor"}-total`}
            className={`text-[11px] font-semibold ${covers3Years ? "text-emerald-700" : "text-forest/55"}`}
          >
            {years > 0 ? `${years} yr${years === 1 ? "" : "s"} ` : ""}{months} mo covered
            {!covers3Years && <span className="text-forest/40"> · 3 years recommended</span>}
          </span>
        )}
      </div>
    </div>
  );
};

const CommitmentsEditor = ({ value, onChange, disabled, testid }) => {
  const rows = Array.isArray(value) ? value : [];
  const update = (idx, k, v) => {
    const next = rows.slice();
    next[idx] = { ...(next[idx] || {}), [k]: v };
    onChange(next);
  };
  const add = () => onChange([...rows, {}]);
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));
  return (
    <div className="sm:col-span-2 space-y-2" data-testid={testid || "commitments-editor"}>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-[10px] tracking-[0.14em] uppercase text-forest/55">
              <th className="text-left px-2 py-1">Applicant</th>
              <th className="text-left px-2 py-1">Type</th>
              <th className="text-left px-2 py-1">Company</th>
              <th className="text-right px-2 py-1">Monthly £</th>
              <th className="text-right px-2 py-1">Outstanding £</th>
              <th className="text-right px-2 py-1">Months Left</th>
              <th className="text-left px-2 py-1">End Date</th>
              <th className="text-right px-2 py-1">Rate %</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-forest/8">
                {["applicant", "type", "company"].map((k) => (
                  <td key={k} className="px-1 py-1">
                    <input disabled={disabled} value={r[k] || ""} onChange={(e) => update(i, k, e.target.value)}
                      className="w-full bg-cream-3 border border-forest/15 px-2 py-1.5 focus:border-gold focus:outline-none" />
                  </td>
                ))}
                {["monthly", "outstanding", "months_left"].map((k) => (
                  <td key={k} className="px-1 py-1">
                    <input type="number" disabled={disabled} value={r[k] || ""} onChange={(e) => update(i, k, e.target.value)}
                      className="w-full bg-cream-3 border border-forest/15 px-2 py-1.5 text-right focus:border-gold focus:outline-none" />
                  </td>
                ))}
                <td className="px-1 py-1">
                  <input type="date" disabled={disabled} value={r.end_date || ""} onChange={(e) => update(i, "end_date", e.target.value)}
                    className="w-full bg-cream-3 border border-forest/15 px-2 py-1.5 focus:border-gold focus:outline-none" />
                </td>
                <td className="px-1 py-1">
                  <input type="number" step="0.01" disabled={disabled} value={r.rate || ""} onChange={(e) => update(i, "rate", e.target.value)}
                    className="w-full bg-cream-3 border border-forest/15 px-2 py-1.5 text-right focus:border-gold focus:outline-none" />
                </td>
                <td className="px-1 py-1 text-center">
                  <button type="button" disabled={disabled} onClick={() => remove(i)} className="p-1.5 text-forest/50 hover:text-red-600">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={9} className="px-2 py-3 text-sage text-center">No commitments yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {!disabled && (
        <button type="button" onClick={add} className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-forest hover:text-gold-dark">
          <Plus size={13} /> Add Commitment
        </button>
      )}
    </div>
  );
};

const SectionBody = ({ section, data, patch, disabled, errors, isJoint }) => {
  const sectionData = data[section.id] || {};

  // Per-applicant field renderer — handles the special `address_history` type
  // so it stays visually glued to the applicant's other fields.
  const renderApplicantField = (f, aKey, aData) => {
    if (f.type === "address_history") {
      return (
        <FieldWrap key={f.id} label={f.label} help={f.help} full required={false}>
          <AddressHistoryEditor
            value={aData[f.id]}
            disabled={disabled}
            testid={`${section.id}-${aKey}-address-history`}
            onChange={(v) =>
              patch({ [section.id]: { ...sectionData, [aKey]: { ...aData, [f.id]: v } } })
            }
          />
        </FieldWrap>
      );
    }
    return (
      <FieldWrap
        key={f.id}
        label={f.label}
        help={f.help}
        full={f.full}
        required={f.required}
        error={errors?.[`${section.id}.${aKey}.${f.id}`]}
        dateHint={f.type === "date" && aData[f.id] ? fmtDDMMYYYY(aData[f.id]) : null}
      >
        <Field
          field={f}
          value={aData[f.id]}
          disabled={disabled}
          path={`${section.id}-${aKey}`}
          onChange={(v) =>
            patch({ [section.id]: { ...sectionData, [aKey]: { ...aData, [f.id]: v } } })
          }
        />
      </FieldWrap>
    );
  };

  if (section.applicants) {
    const apps = sectionData;
    // Single-applicant mode: render the fields once, writing into applicant_1
    // so backend seeding and PDF rendering keep working unchanged.
    if (!isJoint) {
      const aData = apps.applicant_1 || {};
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {section.fields.map((f) => shouldShow(f, aData) && renderApplicantField(f, "applicant_1", aData))}
        </div>
      );
    }

    // Joint mode — stacked sections per applicant for easier mobile fill.
    const copyFromA1 = () =>
      patch({ [section.id]: { ...apps, applicant_2: { ...(apps.applicant_1 || {}) } } });
    const a2HasData = Object.values(apps.applicant_2 || {}).some((v) => v && String(v).trim());
    return (
      <div className="space-y-8">
        {["applicant_1", "applicant_2"].map((aKey, idx) => {
          const aData = apps[aKey] || {};
          return (
            <div key={aKey} className="space-y-3" data-testid={`${section.id}-${aKey}-block`}>
              <div className="flex items-end justify-between gap-3 border-b border-gold/40 pb-2">
                <h4 className="text-[11.5px] tracking-[0.22em] uppercase font-bold text-gold-dark">
                  {idx === 0 ? "1st Applicant" : "2nd Applicant"}
                </h4>
                {idx === 1 && section.allowCopy && !disabled && (
                  <button
                    type="button"
                    onClick={copyFromA1}
                    data-testid={`${section.id}-copy-a1`}
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.14em] uppercase text-forest hover:text-gold-dark border border-forest/20 px-3 py-1.5"
                    title="Copy applicant 1's answers into applicant 2 as a starting point"
                  >
                    <CopyIcon size={12} /> {a2HasData ? "Replace with Applicant 1" : "Same as Applicant 1"}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {section.fields.map((f) => shouldShow(f, aData) && renderApplicantField(f, aKey, aData))}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Commitments section — in joint mode, split into two per-applicant tables
  // so each applicant's spending is captured and labelled separately.
  const hasCommitments = section.fields.some((f) => f.type === "commitments");
  if (hasCommitments && isJoint) {
    const nonCommitmentFields = section.fields.filter((f) => f.type !== "commitments");
    const commitmentField = section.fields.find((f) => f.type === "commitments");
    const a1Rows = sectionData.applicant_1_commitments;
    const a2Rows = sectionData.applicant_2_commitments;
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {nonCommitmentFields.map((f) => shouldShow(f, sectionData) && (
            <FieldWrap
              key={f.id}
              label={f.label}
              help={f.help}
              full={f.full}
              required={f.required}
              error={errors?.[`${section.id}.${f.id}`]}
            >
              <Field
                field={f}
                value={sectionData[f.id]}
                disabled={disabled}
                path={section.id}
                onChange={(v) => patch({ [section.id]: { ...sectionData, [f.id]: v } })}
              />
            </FieldWrap>
          ))}
        </div>
        {["applicant_1_commitments", "applicant_2_commitments"].map((key, idx) => (
          <div key={key} className="space-y-3" data-testid={`${section.id}-${key}-block`}>
            <div className="border-b border-gold/40 pb-2">
              <h4 className="text-[11.5px] tracking-[0.22em] uppercase font-bold text-gold-dark">
                {idx === 0 ? "1st Applicant — Commitments" : "2nd Applicant — Commitments"}
              </h4>
            </div>
            <CommitmentsEditor
              value={idx === 0 ? a1Rows : a2Rows}
              disabled={disabled}
              testid={`commitments-editor-${idx === 0 ? "a1" : "a2"}`}
              onChange={(v) => patch({ [section.id]: { ...sectionData, [key]: v } })}
            />
          </div>
        ))}
        {void commitmentField}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
      {section.fields.map((f) => {
        if (!shouldShow(f, sectionData)) return null;
        if (f.type === "commitments") {
          return (
            <CommitmentsEditor
              key={f.id}
              value={sectionData[f.id]}
              disabled={disabled}
              onChange={(v) => patch({ [section.id]: { ...sectionData, [f.id]: v } })}
            />
          );
        }
        if (f.type === "signature") return null;
        return (
          <FieldWrap
            key={f.id}
            label={f.label}
            help={f.help}
            full={f.full}
            required={f.required}
            error={errors?.[`${section.id}.${f.id}`]}
            dateHint={f.type === "date" && sectionData[f.id] ? fmtDDMMYYYY(sectionData[f.id]) : null}
          >
            <Field
              field={f}
              value={sectionData[f.id]}
              disabled={disabled}
              path={section.id}
              onChange={(v) => patch({ [section.id]: { ...sectionData, [f.id]: v } })}
            />
          </FieldWrap>
        );
      })}
    </div>
  );
};

// Render a compact read-only summary of every answered field.
const ReviewPane = ({ sections, data, onJumpTo, isJoint }) => {
  const renderValue = (f, v) => {
    if (f.type === "address_history") {
      const rows = Array.isArray(v) ? v.filter((r) => r && (r.address || r.postcode || r.from || r.to)) : [];
      if (!rows.length) return <em className="text-forest/40">—</em>;
      return (
        <ul className="list-disc pl-5 space-y-0.5">
          {rows.map((r, i) => (
            <li key={i} className="text-forest/90">
              {[r.address, r.postcode].filter(Boolean).join(", ")}
              {(r.from || r.to) && (
                <span className="text-forest/55">
                  {" "}· {fmtDDMMYYYY(r.from) || "?"} – {fmtDDMMYYYY(r.to) || "present"}
                </span>
              )}
            </li>
          ))}
        </ul>
      );
    }
    if (v == null || v === "") return <em className="text-forest/40">—</em>;
    if (f.type === "date") return fmtDDMMYYYY(v);
    if (f.type === "money") return fmtGBP(v);
    return String(v);
  };
  const renderCommitmentList = (rows, emptyLabel) => (
    (Array.isArray(rows) && rows.length > 0) ? (
      <ul className="text-[12.5px] list-disc pl-5 space-y-1">
        {rows.map((row, i) => (
          <li key={i}>
            {row.applicant || "?"} · {row.type || "—"} · {row.company || "—"} · {fmtGBP(row.monthly)}/mo
          </li>
        ))}
      </ul>
    ) : <p className="text-[12px] text-forest/40 italic">{emptyLabel || "No commitments recorded."}</p>
  );
  const sectionBlocks = sections
    .filter((s) => !s.isDeclaration && !s.isReview)
    .map((s, idx) => {
      const sd = data[s.id] || {};
      return (
        <div key={s.id} className="bg-white border border-forest/10 p-5 sm:p-6" data-testid={`review-section-${s.id}`}>
          <div className="flex items-start justify-between gap-4 mb-3 border-b border-forest/10 pb-3">
            <h3 className="font-serif text-lg text-forest">{s.title}</h3>
            <button
              type="button"
              onClick={() => onJumpTo(idx + 1)}
              data-testid={`review-edit-${s.id}`}
              className="text-[11px] font-bold tracking-[0.14em] uppercase text-forest hover:text-gold-dark"
            >
              Edit
            </button>
          </div>
          {s.applicants ? (
            isJoint ? (
              <div className="space-y-5">
                {["applicant_1", "applicant_2"].map((aKey, aIdx) => {
                  const aData = sd[aKey] || {};
                  return (
                    <div key={aKey}>
                      <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-gold-dark mb-2">
                        {aIdx === 0 ? "1st Applicant" : "2nd Applicant"}
                      </p>
                      <dl className="space-y-1.5 text-[12.5px]">
                        {s.fields
                          .filter((f) => f.type !== "signature" && shouldShow(f, aData))
                          .map((f) => (
                            <div key={f.id} className="flex gap-2 border-b border-forest/5 pb-1.5">
                              <dt className="text-forest/60 min-w-[45%]">{f.label}</dt>
                              <dd className="text-forest flex-1 break-words">{renderValue(f, aData[f.id])}</dd>
                            </div>
                          ))}
                      </dl>
                    </div>
                  );
                })}
              </div>
            ) : (
              <dl className="space-y-1.5 text-[13px]">
                {s.fields
                  .filter((f) => f.type !== "signature" && shouldShow(f, sd.applicant_1 || {}))
                  .map((f) => (
                    <div key={f.id} className="flex gap-2 border-b border-forest/5 pb-1.5">
                      <dt className="text-forest/60 min-w-[45%]">{f.label}</dt>
                      <dd className="text-forest flex-1 break-words">{renderValue(f, (sd.applicant_1 || {})[f.id])}</dd>
                    </div>
                  ))}
              </dl>
            )
          ) : s.fields.some((f) => f.type === "commitments") ? (
            <div className="space-y-2 text-[13px]">
              <dl className="space-y-1.5">
                {s.fields.filter((f) => f.type !== "commitments" && shouldShow(f, sd)).map((f) => (
                  <div key={f.id} className="flex gap-2 border-b border-forest/5 pb-1.5">
                    <dt className="text-forest/60 min-w-[45%]">{f.label}</dt>
                    <dd className="text-forest flex-1 break-words">{renderValue(f, sd[f.id])}</dd>
                  </div>
                ))}
              </dl>
              {isJoint ? (
                <div className="space-y-3 mt-3">
                  <div>
                    <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-gold-dark mb-1">1st Applicant — Commitments</p>
                    {renderCommitmentList(sd.applicant_1_commitments)}
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-gold-dark mb-1">2nd Applicant — Commitments</p>
                    {renderCommitmentList(sd.applicant_2_commitments)}
                  </div>
                </div>
              ) : (
                <div>
                  <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-gold-dark mt-3 mb-1">Commitments</p>
                  {renderCommitmentList(sd.commitments_table)}
                </div>
              )}
            </div>
          ) : (
            <dl className="space-y-1.5 text-[13px]">
              {s.fields.filter((f) => f.type !== "signature" && shouldShow(f, sd)).map((f) => (
                <div key={f.id} className="flex gap-2 border-b border-forest/5 pb-1.5">
                  <dt className="text-forest/60 min-w-[45%]">{f.label}</dt>
                  <dd className="text-forest flex-1 break-words">{renderValue(f, sd[f.id])}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      );
    });
  return (
    <div className="space-y-4" data-testid="review-pane">
      <p className="text-sage text-[13.5px] bg-white border border-forest/10 px-5 py-3">
        Please check your answers below. Tap <strong>Edit</strong> on any section to jump back and change it — your progress is kept.
      </p>
      {sectionBlocks}
    </div>
  );
};

const Done = ({ type, signedAt, customerName }) => {
  const label = findProduct(type || "mortgage").label;
  const legacy = type === "mortgage" || type === "remortgage";
  const niceLabel = legacy ? (type === "remortgage" ? "remortgage" : "mortgage") : label;
  return (
    <div className="max-w-xl mx-auto text-center py-20 space-y-5 px-6">
      <div className="w-14 h-14 rounded-full bg-forest text-cream mx-auto flex items-center justify-center">
        <Check size={28} strokeWidth={2.2} />
      </div>
      <h1 className="font-serif text-3xl text-forest">Thank you, {customerName || "and welcome"}</h1>
      <p className="text-sage leading-relaxed">
        Your {niceLabel} application has been signed and received by Elin's Consultancy on{" "}
        <strong className="text-forest">{fmtDDMMYYYY(signedAt)}</strong>. A member of our team will be in touch with next steps.
      </p>
    </div>
  );
};

const validateSection = (section, data, isJoint) => {
  const errors = {};
  if (!section || section.isDeclaration || section.isReview) return errors;
  if (!Array.isArray(section.fields)) return errors;
  const sectionData = data[section.id] || {};
  const checkField = (f, scope, prefix) => {
    if (!shouldShow(f, scope)) return;
    const v = scope[f.id];
    if (f.required && (v == null || String(v).trim() === "")) {
      errors[`${prefix}${f.id}`] = "This answer is required to continue.";
      return;
    }
    if (!v) return;
    if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
      errors[`${prefix}${f.id}`] = "Please enter a valid email address.";
    if (f.type === "postcode" && !/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(String(v).toUpperCase()))
      errors[`${prefix}${f.id}`] = "Please enter a valid UK postcode, e.g. SW1A 1AA.";
    if ((f.type === "money" || f.type === "number") && isNaN(Number(v)))
      errors[`${prefix}${f.id}`] = "Please enter a number.";
  };
  if (section.applicants) {
    const applicants = isJoint ? ["applicant_1", "applicant_2"] : ["applicant_1"];
    for (const aKey of applicants) {
      const scope = sectionData[aKey] || {};
      // Only enforce required-ness on applicant 1; applicant 2 is optional
      // (in joint mode A2 is still soft-validated — admin can nudge later).
      const fieldsToCheck = aKey === "applicant_1" ? section.fields : section.fields.filter((f) => !f.required);
      fieldsToCheck.forEach((f) => checkField(f, scope, `${section.id}.${aKey}.`));
    }
  } else {
    section.fields.filter((f) => f.type !== "commitments" && f.type !== "signature").forEach((f) =>
      checkField(f, sectionData, `${section.id}.`)
    );
  }
  return errors;
};

export default function PublicForm() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [data, setData] = useState({});
  const [type, setType] = useState("mortgage");
  const [applicantType, setApplicantType] = useState("single");
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [sig1, setSig1] = useState("");
  const [sig2, setSig2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const saveTimer = useRef(null);
  const sig1Ref = useRef(null);
  const sig2Ref = useRef(null);

  useEffect(() => {
    axios.get(`${API}/forms/public/${token}`).then(({ data: d }) => {
      setForm(d);
      setData(d.data || {});
      setType(d.type || "mortgage");
      setApplicantType(d.applicant_type || "single");
    }).catch(() => toast.error("This link is invalid or has expired."));
  }, [token]);

  const isJoint = applicantType === "joint";

  const sections = useMemo(() => sectionsForType(type), [type]);
  const steps = useMemo(() => [{ id: "type", title: "Application Type" }, ...sections], [sections]);
  const totalSteps = steps.length;
  const current = steps[step];
  const isSigned = form?.status === "signed";

  const scheduleSave = (nextData, nextType) => {
    if (isSigned) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await axios.patch(`${API}/forms/public/${token}`, {
          data: nextData,
          type: nextType ?? type,
          customer_name:
            nextData?.advice?.customer_names ||
            [nextData?.applicant?.applicant_1?.first_name, nextData?.applicant?.applicant_1?.surname]
              .filter(Boolean).join(" "),
        });
      } catch {
        toast.error("Could not save progress. Check your connection.");
      } finally {
        setSaving(false);
      }
    }, 500);
  };

  const patch = (patchObj) => {
    const next = { ...data, ...patchObj };
    setData(next);
    // Clear errors for any field just edited
    const touched = Object.keys(patchObj).flatMap((k) => Object.keys(patchObj[k] || {}).map((f) => `${k}.${f}`));
    if (touched.length) {
      setErrors((prev) => {
        const copy = { ...prev };
        for (const key of Object.keys(copy)) if (touched.some((t) => key.startsWith(t))) delete copy[key];
        return copy;
      });
    }
    scheduleSave(next);
  };

  const changeType = (t) => {
    setType(t);
    scheduleSave(data, t);
  };

  const goNext = () => {
    if (current && !current.isReview && !current.isDeclaration) {
      const sectionErrors = validateSection(current, data, isJoint);
      if (Object.keys(sectionErrors).length) {
        setErrors(sectionErrors);
        toast.error("A few answers need a tweak before we continue.");
        return;
      }
    }
    setErrors({});
    setStep((s) => Math.min(totalSteps - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/forms/public/${token}/sign`, {
        signature_applicant_1: sig1 || sig1Ref.current?.toDataURL(),
        signature_applicant_2: sig2 || sig2Ref.current?.toDataURL(),
        confirmed,
      });
      const { data: fresh } = await axios.get(`${API}/forms/public/${token}`);
      setForm(fresh);
      toast.success("Form submitted — thank you.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!form) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Loader2 className="animate-spin text-forest" />
      </div>
    );
  }

  if (isSigned)
    return (
      <div className="min-h-screen bg-cream">
        <Done type={form.type} signedAt={form.signed_at} customerName={form.customer_name} />
      </div>
    );

  const progressPct = Math.round(((step + 1) / totalSteps) * 100);
  const isDeclaration = current?.isDeclaration;
  const isReview = current?.isReview;
  const isAdminFilling = typeof window !== "undefined" && window.location.hash === "#admin";

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="bg-forest text-cream px-5 py-4 border-b border-gold/30">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo.png" alt="Elin's Consultancy" className="h-12 w-auto" />
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-gold-soft">Elin's Consultancy</p>
              <h1 className="font-serif text-lg sm:text-xl mt-0.5 truncate">{(type === "mortgage" || type === "remortgage") ? "Mortgage Application & Fact Find" : `${findProduct(type).label} Application`}</h1>
            </div>
          </div>
          <div className="text-right text-[11px] text-cream/70 whitespace-nowrap">
            {saving ? "Saving…" : "Saved"}
          </div>
        </div>
      </header>

      <div className="sticky top-0 z-10 bg-cream border-b border-forest/10">
        {isAdminFilling && (
          <div data-testid="admin-filling-banner" className="bg-gold/15 border-b border-gold/30 text-forest px-5 py-2">
            <div className="max-w-4xl mx-auto text-[12px] font-semibold tracking-[0.08em]">
              Filling on behalf of the client — close this tab when you're done. The customer will not see this banner.
            </div>
          </div>
        )}
        <div className="max-w-4xl mx-auto px-5 pt-3 pb-2">
          <div className="flex items-center justify-between text-[10.5px] text-forest/70 mb-2">
            <span className="font-bold tracking-[0.22em] uppercase text-gold-dark">
              Step {step + 1} of {totalSteps}
              {current?.title && (
                <span className="ml-2 normal-case tracking-normal text-forest/60 font-semibold">· {current.title}</span>
              )}
            </span>
            <span className="font-semibold text-forest/60">{progressPct}%</span>
          </div>

          {/* Compact step rail — numbered pills, horizontally scrollable on phone */}
          <ol className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-2" aria-label="Form sections">
            {sections.map((s, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <li key={s.id} className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    disabled={!done}
                    onClick={() => done && setStep(i)}
                    data-testid={`form-step-rail-${i}`}
                    title={s.title}
                    className={`flex items-center gap-1.5 px-2.5 py-1 border rounded-sm text-[11px] font-semibold transition-colors ${
                      active
                        ? "border-gold bg-gold/15 text-forest"
                        : done
                          ? "border-forest/20 bg-white text-forest/70 hover:border-forest/40 cursor-pointer"
                          : "border-forest/10 bg-white text-forest/40"
                    }`}
                  >
                    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9.5px] font-bold ${
                      active ? "bg-forest text-cream"
                      : done ? "bg-emerald-700 text-cream"
                      : "bg-cream-3 text-forest/50 border border-forest/15"
                    }`}>
                      {done ? "✓" : i + 1}
                    </span>
                    <span className="whitespace-nowrap max-w-[140px] truncate">{s.title}</span>
                  </button>
                  {i < sections.length - 1 && <span className="w-2 h-px bg-forest/15" />}
                </li>
              );
            })}
          </ol>

          <div className="h-1 bg-cream-3 rounded-full overflow-hidden">
            <div className="h-full bg-gold transition-all" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-5 sm:px-6 py-8 pb-32">
        {step === 0 ? (
          <section className="space-y-5 max-w-xl">
            <h2 className="font-serif text-2xl text-forest">What are you applying for?</h2>
            {(type === "mortgage" || type === "remortgage") ? (
              <>
                <p className="text-sage">Pick one so we only ask the questions that apply to you.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { id: "mortgage", title: "Mortgage", sub: "I am buying a property" },
                    { id: "remortgage", title: "Remortgage", sub: "I am remortgaging an existing property" },
                  ].map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => changeType(o.id)}
                      data-testid={`form-type-${o.id}`}
                      className={`text-left border px-5 py-4 transition-colors ${
                        type === o.id ? "border-gold bg-gold/10" : "border-forest/15 bg-white hover:border-forest/40"
                      }`}
                    >
                      <p className="font-serif text-lg text-forest">{o.title}</p>
                      <p className="text-[12px] text-sage mt-0.5">{o.sub}</p>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="text-sage">We've prepared this application for the product below. Tap Continue to start.</p>
                <div
                  data-testid={`form-type-${type}`}
                  className="border border-gold bg-gold/10 px-5 py-4 rounded-sm"
                >
                  <p className="font-serif text-lg text-forest">{findProduct(type).label}</p>
                  <p className="text-[12px] text-sage mt-0.5">We'll only ask the questions that apply to this product.</p>
                </div>
              </>
            )}
          </section>
        ) : (
          <section className="space-y-6">
            <div className="border-l-[3px] border-gold pl-4">
              <p className="text-[10px] tracking-[0.26em] uppercase font-semibold text-gold-dark">
                Section {step} of {totalSteps - 1}
              </p>
              <h2 className="font-serif text-2xl sm:text-[28px] text-forest mt-1 tracking-tight">{current.title}</h2>
              {current.description && <p className="text-sage text-[13.5px] mt-1.5 leading-relaxed">{current.description}</p>}
            </div>

            {isReview ? (
              <ReviewPane
                sections={sections}
                data={data}
                isJoint={isJoint}
                onJumpTo={(idx) => {
                  setStep(idx);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            ) : isDeclaration ? (
              <div className="space-y-6">
                <p className="bg-white border border-forest/15 px-5 py-4 text-[14px] leading-relaxed text-ink">
                  {current.declaration}
                </p>
                <div className={`grid grid-cols-1 ${isJoint ? "md:grid-cols-2" : ""} gap-6`}>
                  <div>
                    <p className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">
                      {isJoint ? "Applicant 1 Signature" : "Signature"} <span className="text-gold-dark">*</span>
                    </p>
                    <SignaturePad ref={sig1Ref} value={sig1} onEnd={setSig1} testid="form-signature-1" />
                  </div>
                  {isJoint && (
                    <div>
                      <p className="text-[10.5px] tracking-[0.18em] uppercase font-bold text-forest/60 mb-1.5">
                        Applicant 2 Signature <span className="text-gold-dark">*</span>
                      </p>
                      <SignaturePad ref={sig2Ref} value={sig2} onEnd={setSig2} testid="form-signature-2" />
                    </div>
                  )}
                </div>
                <label className="flex items-start gap-3 bg-white border border-forest/15 px-4 py-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    data-testid="form-confirm"
                    className="mt-1 w-4 h-4 accent-forest"
                  />
                  <span className="text-[13.5px] text-ink leading-relaxed">
                    I/We confirm that the information provided in this form is, to the best of my/our knowledge,
                    complete and accurate, and I/we authorise Elin's Consultancy to act on my/our behalf.
                  </span>
                </label>
              </div>
            ) : (
              <SectionBody section={current} data={data} patch={patch} disabled={isSigned} errors={errors} isJoint={isJoint} />
            )}
          </section>
        )}
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-forest/10 px-5 py-3.5">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={step === 0}
            onClick={() => { setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            data-testid="form-back"
            className="btn-ghost-dark disabled:opacity-30 inline-flex items-center gap-1.5"
          >
            <ChevronLeft size={14} /> Back
          </button>
          <p className="text-[11px] text-sage hidden sm:block">
            Progress is saved automatically — you can close this page and return later with the same link.
          </p>
          {isDeclaration ? (
            <button
              type="button"
              disabled={!confirmed || !sig1 || (isJoint && !sig2) || submitting}
              onClick={submit}
              data-testid="form-submit"
              className="btn-gold inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
              Submit &amp; Sign
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              data-testid="form-next"
              className="btn-gold inline-flex items-center gap-1.5"
            >
              {isReview ? "Continue to Sign" : "Continue"} <ChevronRight size={14} />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}

void MORTGAGE_FORM_SECTIONS;
